import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ListView, ListViewColumn, ListViewColumnType, FilterType, initListView } from '../../UI/view/list-view';
import { FormItemType } from '../../UI/view/component-map';
import { formatDate, formatDateTimeNoTZ } from '../../../services/format';
import { selectLoggedInUserPreferences } from '../../../store/slices/auth-slice';
import { DEFAULT_BOOKING_DAYS } from '../../../services/requests/bookings/bookings-requests';
import {
  bookingsPermissions,
  newBookingsDS,
  requestedBookingsDS,
  containerPendingBookingsDS,
  completedBookingsDS,
  priorBookingsDS,
  cancelledPendingBookingsDS,
  cancelledConfirmedBookingsDS,
  searchBookingsDS,
  multipleBookingsDS,
} from './bookings-datasource';
import { bookingEditPath, legacyConfirmCancelUrl } from './bookings-links';

// Bookings_ConfirmCancel is not ported yet and still opens on the legacy VTS site.
const bookingLink = (urlFor: (id: any) => string, style?: (row: any) => any, title?: (row: any) => string | undefined) =>
  ({ value, row }: any) => {
    const url = urlFor(value);
    const props = { style: style?.(row?.original), title: title?.(row?.original) };
    return url.startsWith('/')
      ? React.createElement(Link, { to: url, ...props }, value)
      : React.createElement('a', { href: url, target: '_blank', rel: 'noopener noreferrer', ...props }, value);
  };

// Legacy LastUpdated is the raw stored datetime (no timezone conversion).
const DateTimeCell = ({ value }: any) => {
  const { datePreference } = (useSelector(selectLoggedInUserPreferences) as any) ?? {};
  return value ? formatDateTimeNoTZ(value, datePreference) : '';
};
const dateTimeCell = ({ value }: any) => React.createElement(DateTimeCell, { value });

const bookingIdColumn = (urlFor = bookingEditPath, extra: Partial<ListViewColumn> = {}): ListViewColumn => ({
  Header: 'Booking ID',
  accessor: 'bookingId',
  Cell: bookingLink(urlFor),
  ...extra,
});
const poNumber: ListViewColumn = { Header: 'PO Number', accessor: 'poNumber' };
const entryDate: ListViewColumn = { Header: 'Entry Date', accessor: 'lastUpdated', Cell: dateTimeCell };
const customerName: ListViewColumn = { Header: 'Customer Name', accessor: 'customerName' };
const containerId = (header: string): ListViewColumn => ({ Header: header, accessor: 'containerId' });
const bookingAgent: ListViewColumn = { Header: 'Booking Agent', accessor: 'bookingAgent' };
const dateColumn = (header: string, accessor: string, extra: Partial<ListViewColumn> = {}): ListViewColumn =>
  ({ Header: header, accessor, type: ListViewColumnType.date, ...extra });

// gvContainerPending / gvCompletedBookings / gvPriorBookings / gvCancelledPending layout.
const scheduleColumns = (bookingId: ListViewColumn, portCutoff: ListViewColumn = dateColumn('Port Cutoff Date', 'usPortCutoffDate')): ListViewColumn[] => [
  bookingId,
  poNumber,
  containerId('Container ID'),
  customerName,
  bookingAgent,
  dateColumn("Cust Req'd Date", 'dateRequested'),
  portCutoff,
  dateColumn('Sail Date', 'sailingDate'),
  dateColumn('ETA Customer', 'customerPortEta'),
];

const listBase = {
  permissions: bookingsPermissions,
  staleRefetchMs: 5 * 60 * 1000,
};

// --- gvNewBookings ---------------------------------------------------------
export const NewBookingsList = initListView({
  ...listBase,
  columns: [
    bookingIdColumn(),
    poNumber,
    entryDate,
    customerName,
    containerId('Container ID'),
    bookingAgent,
    dateColumn('Cust Req Date', 'dateRequested'),
  ],
} as ListView, newBookingsDS);

// --- gvRequestedBookings ---------------------------------------------------
// Legacy RowCreated: requested 14+ days ago and still missing booking number /
// container / port cutoff => Booking ID red + bold with an explanatory tooltip.
const requestedAttentionTip = 'Requested Customer Date indicated booking should have moved forward. Check that ContainerID, Booking Number, and cutoff Date are populated';
export const RequestedBookingsList = initListView({
  ...listBase,
  columns: [
    bookingIdColumn(bookingEditPath, {
      Cell: bookingLink(
        bookingEditPath,
        (row) => (row?.needsAttention ? { color: 'red', fontWeight: 'bold' } : undefined),
        (row) => (row?.needsAttention ? requestedAttentionTip : undefined),
      ),
    }),
    poNumber,
    entryDate,
    customerName,
    containerId('ContainerID'),
    bookingAgent,
    dateColumn('Cust Req Date', 'dateRequested'),
  ],
} as ListView, requestedBookingsDS);

// --- gvContainerPending ----------------------------------------------------
export const ContainerPendingBookingsList = initListView({
  ...listBase,
  columns: scheduleColumns(bookingIdColumn()),
} as ListView, containerPendingBookingsDS);

// --- gvCompletedBookings ---------------------------------------------------
export const CompletedBookingsList = initListView({
  ...listBase,
  columns: scheduleColumns(bookingIdColumn()),
} as ListView, completedBookingsDS);

// --- gvPriorBookings + "Show Records for last N days" ----------------------
// Legacy SetConditionalRowStyle: Port Cutoff Date red when cutoff + 2 days >= now.
const daysFilter = {
  fieldName: 'days',
  type: FilterType.number,
  label: 'Show Records for last (days)',
  filterValue: DEFAULT_BOOKING_DAYS,
  minValue: 0,
  maxValue: 9999,
  requestParam: true,
};
export const PriorBookingsList = initListView({
  ...listBase,
  filters: [daysFilter],
  columns: scheduleColumns(
    bookingIdColumn(),
    dateColumn('Port Cutoff Date', 'usPortCutoffDate', {
      validation: { cutoff: { handler: (row) => !row?.isCutoffDate, message: '' } },
    }),
  ),
} as ListView, priorBookingsDS);

// --- gvCancelledPending (edit goes to the confirm-cancel page) -------------
export const CancelledPendingBookingsList = initListView({
  ...listBase,
  columns: scheduleColumns(bookingIdColumn(legacyConfirmCancelUrl)),
} as ListView, cancelledPendingBookingsDS);

// --- gvCancelledConfirmed + "Show Records for last N days" -----------------
export const CancelledConfirmedBookingsList = initListView({
  ...listBase,
  filters: [daysFilter],
  columns: [
    bookingIdColumn(legacyConfirmCancelUrl),
    poNumber,
    containerId('Container ID'),
    customerName,
    bookingAgent,
    dateColumn("Cust Req'd Date", 'dateRequested'),
    { Header: 'Cancelled Comments', accessor: 'cancelledComments' },
    { Header: 'Last Modified', accessor: 'lastUpdated', Cell: dateTimeCell },
  ],
} as ListView, cancelledConfirmedBookingsDS);

// --- pnlBookingFilter + gvBookings (status / created date range search) ----
// Option values are sent to the stored procedures as @Status, so the legacy
// spelling ("Cancelled Pendding") is preserved.
const bookingStatuses = [
  'All Bookings',
  'New Bookings',
  'Booking Requested',
  'Container Pending',
  'Completed Bookings',
  'Prior Bookings',
  'Cancelled Pendding',
  'Cancelled Confirmed',
];
const isoDate = (d: Date) => {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const daysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};
export const SearchBookingsList = initListView({
  ...listBase,
  filters: [
    {
      fieldName: 'status',
      type: FilterType.combo,
      label: 'Select Status',
      filterValue: 'All Bookings',
      options: () => bookingStatuses,
      requestParam: true,
    },
    { fieldName: 'startDate', type: FilterType.date, label: 'Created Date From', filterValue: isoDate(daysAgo(90)), requestParam: true },
    { fieldName: 'endDate', type: FilterType.date, label: 'To', filterValue: isoDate(new Date()), requestParam: true },
  ],
  columns: [
    // Legacy hides the CancelledFlag cell, so the redirect-to-confirm-cancel check never fires here.
    bookingIdColumn(),
    { Header: 'Booking Number', accessor: 'bookingNumber' },
    customerName,
    containerId('ContainerID'),
    bookingAgent,
    dateColumn('Date Requested', 'dateRequested'),
    dateColumn('Sailing Date', 'sailingDate'),
  ],
} as ListView, searchBookingsDS);

// --- gvMultipleBookings (always shown above the grids, red text) -----------
const redCell = (format?: (v: any) => any) => ({ value }: any) =>
  React.createElement('span', { style: { color: 'red' } }, format ? format(value) : value);
export const MultipleBookingsList = initListView({
  ...listBase,
  hideGlobalFilter: true,
  columns: [
    { Header: 'Customer', accessor: 'customer', Cell: redCell() },
    { Header: 'Sailing Date', accessor: 'sailingDate', Cell: redCell((v) => (v ? formatDate(v) : '')) },
    { Header: 'Number of Bookings', accessor: 'counter', Cell: redCell() },
    { Header: 'Booking ID List', accessor: 'bookingNumberList', Cell: redCell() },
  ],
} as ListView, multipleBookingsDS);

export const addBookingButton = {
  type: FormItemType.button,
  name: 'addBooking',
  label: 'Add',
  variant: 'primary',
  permissions: bookingsPermissions,
  handler: ({ redirect }: any) => redirect('/bookings/create'),
};
